import { useId, useState, type ChangeEvent, type FormEvent } from "react";
import cx from "classnames";
import { Button, StateInfoBox } from "@/components";
import { isValidEmail } from "@/helpers";
import { useAmlHistoryStore } from "@/store";

import styles from "./PdfReportForm.module.sass";

interface PdfReportFormProps {
  itemId: string;
  className?: string;
}

const PdfReportForm = ({ itemId, className }: PdfReportFormProps) => {
  const { pdfReportSubmittedIds, sendPdfReport } = useAmlHistoryStore();
  const titleId = useId();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isSubmitted = pdfReportSubmittedIds.includes(itemId);
  const canSubmit = isValidEmail(email) && !isSubmitting;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setError("");
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await sendPdfReport(itemId, email.trim());
    } catch {
      setError("Couldn't send the report. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <StateInfoBox
        type="success"
        title="Result submitted successfully. Check your email"
        className={cx(styles.success, className)}
      />
    );
  }

  return (
    <form className={cx(styles.form, className)} onSubmit={handleSubmit}>
      <div id={titleId} className={styles.title}>
        Want This As A Report?
      </div>

      <input
        type="email"
        name="email"
        autoComplete="email"
        maxLength={254}
        className={styles.input}
        placeholder="your@email.com"
        value={email}
        disabled={isSubmitting}
        aria-labelledby={titleId}
        aria-invalid={!!error}
        onChange={handleChange}
      />

      {!!error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}

      <Button
        variant="outlined"
        type="submit"
        text={isSubmitting ? "Sending…" : "Send PDF Report"}
        disabled={!canSubmit}
        className={styles.submit}
      />
    </form>
  );
};

export default PdfReportForm;
